/**
 * Rascunho local + histórico de sources do gerador de reports.
 *
 * - localStorage: restaura o trabalho em andamento após reload (sources, JSON, capa, etc.)
 * - Firestore admin_report_source_history: histórico reutilizável e detecção de duplicatas
 */

import {
  collection,
  addDoc,
  query,
  orderBy,
  limit,
  getDocs,
  where,
  Timestamp,
  updateDoc,
  doc,
} from 'firebase/firestore';
import { DATABASE } from '../Config/firebase';

const DRAFT_KEY = 'fiddux_pdf_report_draft_v1';
const HISTORY_COLLECTION = 'admin_report_source_history';

export function hashSources(text) {
  const normalized = String(text || '').trim().replace(/\s+/g, ' ');
  if (!normalized) return '';
  let h = 0;
  for (let i = 0; i < normalized.length; i++) {
    h = (h << 5) - h + normalized.charCodeAt(i);
    h |= 0;
  }
  return `src_${Math.abs(h).toString(36)}_${normalized.length}`;
}

export function previewSources(text, maxLen = 120) {
  const t = String(text || '').trim().replace(/\s+/g, ' ');
  if (t.length <= maxLen) return t;
  return `${t.slice(0, maxLen)}…`;
}

export function loadLocalDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function saveLocalDraft(draft) {
  try {
    localStorage.setItem(
      DRAFT_KEY,
      JSON.stringify({ ...draft, saved_at: new Date().toISOString() })
    );
    return true;
  } catch {
    return false;
  }
}

export function clearLocalDraft() {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch {
    /* ignore */
  }
}

export async function loadSourceHistory(max = 20) {
  const q = query(
    collection(DATABASE, HISTORY_COLLECTION),
    orderBy('last_used_at', 'desc'),
    limit(max)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function findSourceByHash(sourcesHash) {
  if (!sourcesHash) return null;
  const q = query(
    collection(DATABASE, HISTORY_COLLECTION),
    where('sources_hash', '==', sourcesHash),
    limit(1)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
}

/**
 * Registra ou atualiza um pacote de sources no histórico.
 */
export async function upsertSourceHistory({
  sources,
  audience,
  reportType,
  aiGenerated = false,
  reportTitle = '',
  reportTitlePt = '',
}) {
  const trimmed = String(sources || '').trim();
  if (trimmed.length < 20) return null;

  const sourcesHash = hashSources(trimmed);
  const existing = await findSourceByHash(sourcesHash);
  const now = Timestamp.now();

  if (existing?.id) {
    await updateDoc(doc(DATABASE, HISTORY_COLLECTION, existing.id), {
      last_used_at: now,
      use_count: (existing.use_count || 1) + 1,
      ...(aiGenerated
        ? {
            ai_generated: true,
            report_title: reportTitle || existing.report_title || '',
            report_title_pt: reportTitlePt || existing.report_title_pt || '',
            last_ai_at: now,
          }
        : {}),
    });
    return { id: existing.id, updated: true, sources_hash: sourcesHash };
  }

  const ref = await addDoc(collection(DATABASE, HISTORY_COLLECTION), {
    sources: trimmed,
    sources_hash: sourcesHash,
    sources_preview: previewSources(trimmed),
    audience: audience || 'client',
    report_type: reportType || 'analysis',
    ai_generated: !!aiGenerated,
    report_title: reportTitle || '',
    report_title_pt: reportTitlePt || '',
    use_count: 1,
    created_at: now,
    last_used_at: now,
    ...(aiGenerated ? { last_ai_at: now } : {}),
  });
  return { id: ref.id, created: true, sources_hash: sourcesHash };
}

export async function touchSourceHistoryEntry(id) {
  if (!id) return;
  await updateDoc(doc(DATABASE, HISTORY_COLLECTION, id), {
    last_used_at: Timestamp.now(),
  });
}
